<template>
  <div class="flex h-10 items-center gap-1 2xl:gap-2">

    <div class="flex h-full w-10 2xl:w-16 items-center justify-center">
      <div v-if="index === 0" class="w-full cursor-default text-center text-xl">
        URL
      </div>

      <vertical-switch v-else-if="index === 1"
                       :value="logicOperator"
                       :top-label="LOGIC_OPERATORS.AND"
                       :bottom-label="LOGIC_OPERATORS.OR"
                       class="w-full"
                       @input="$emit('update:logic-operator', $event)"
      />

      <div v-else
           class="flex w-full cursor-default justify-center px-1"
      >
        <span class="w-full rounded pt-px text-center font-semibold uppercase text-white text-2xs bg-gray">{{ logicOperator }}</span>
      </div>
    </div>

    <base-select
      :value="operand.matchOperator"
      :options="matchOperators"
      class="h-full w-24"
      @input="updateOperand($event, 'matchOperator')"
    />

    <vx-tooltip class="relative flex h-full flex-1 flex-col"
                position="bottom"
                :text="operandErrorDescription"
                :enabled="!!operandErrorDescription"
                ref="inputTooltip"
    >
      <base-input
        ref="textInput"
        :value="operand.text"
        class="h-full"
        :variant="operandErrorDescription ? 'editor-soft-error' : 'editor-soft'"
        placeholder="condition"
        @keyup="updateOperand($event.target.value, 'text', $event)"
      />
    </vx-tooltip>

    <base-tooltip-button variant="hover:transparent-gray"
                         class="flex items-center text-gray-dark h-full w-10"
                         id="delete-button"
                         position="left"
                         :tooltip-enabled="hasMinimalOperands"
                         :is-disabled="hasMinimalOperands"
                         tooltip-text="At least 1 condition must be specified."
                         @click="$emit('remove:operand')"
    >
      <delete-icon class="h-5 text-gray-dark"/>
    </base-tooltip-button>

  </div>
</template>

<script>
import DeleteIcon from '@/assets/images/icons/DeleteIcon'
import BaseSelect from '@/components/base-components/BaseSelect.vue'
import BaseSwitch from '@/components/base-components/BaseSwitch.vue'
import BaseInput from '@/components/base-components/BaseInput.vue'
import VerticalSwitch from '@/components/base-components/VerticalSwitch.vue'
import regularExpressionUtils from '@/utils/key-regex.utils'
import { LOGIC_OPERATORS, MATCH_OPERATORS, CUSTOMERS_URL_LABEL } from '@/utils/key-regex.constants'
import previewModule from '@/store/modules/preview.module'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import keyRegexUtils from '@/utils/key-regex.utils'

export default {
  name: 'ConditionCaseBrowserUrlOperand',

  components: {
    VerticalSwitch,
    BaseInput,
    BaseSwitch,
    BaseSelect,
    DeleteIcon
  },

  props: {
    operand: {
      type: Object,
      default: () => {}
    },
    logicOperator: {
      type: String,
      default: 'and'
    },
    index: {
      type: Number,
      required: true
    },
    hasMinimalOperands: {
      type: Boolean,
      default: false
    },
    stateId: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      matchOperators: Object.values(MATCH_OPERATORS),
      LOGIC_OPERATORS,
      CUSTOMERS_URL_LABEL,
      errors: {
        emptyInput: 'Condition cannot be empty.',
        invalidCharacter: 'Condition can only contain valid URL characters.'
      }
    }
  },

  computed: {
    errorDescription: previewModule.errorDescription,

    operandErrorDescription() {
      if (!chatBubbleModule.isErrorBubbleFocused()) return null

      if (
        this.errorDescription !== 'Url condition is invalid!' ||
        this.isOperandTextValid
      ) {
        return null
      }

      return this.errorDescription
    },

    isOperandTextValid() {
      return keyRegexUtils.isRegexValid(this.operand.text) && !!this.operand.text
    }
  },

  watch: {
    operandErrorDescription: {
      immediate: true,
      async handler(operandErrorDescription) {
        if (!operandErrorDescription) return

        setTimeout(() => {
          this.$refs.textInput.focus()
          this.$refs.inputTooltip.mouseenterx()
        }, 500)
      }
    }
  },

  methods: {
    updateOperand(value, property, e) {
      if (e?.key === 'Shift') return

      const operand = { ...this.operand, [property]: value }
      const operandWithRegex = this.extendByRegex(operand)
      this.$emit('update:operand', operandWithRegex)
    },

    extendByRegex(operand) {
      const regex = operand.matchOperator === MATCH_OPERATORS.WITH ?
        regularExpressionUtils.generateRegexWith(operand.text) :
        regularExpressionUtils.generateRegexWithout(operand.text)

      return { ...operand, regex }
    }
  }
}
</script>
